import React from "react";
import { Edges } from "@react-three/drei";
import { MM, GRIGLIA_LARGHEZZA, GRIGLIA_ALTEZZA, GRIGLIA_CORNICE, C_CORNICE, C_BORDO_SCURO, C_BORDO_CHIARO } from "./constants";

export type GrigliaProps = {
  position: [number, number, number];
  larghezza?: number;
  altezza?: number;
  spessore: number;
  onRemove: () => void;
  isDoorClosed: boolean;
  colore?: string;
};

export const Griglia: React.FC<GrigliaProps> = ({ position, larghezza, altezza, spessore, onRemove, isDoorClosed, colore }) => {
  const gW     = larghezza ?? GRIGLIA_LARGHEZZA;
  const gH     = altezza   ?? GRIGLIA_ALTEZZA;
  const cw     = GRIGLIA_CORNICE;
  const innerW = gW - cw * 2;
  const innerH = gH - cw * 2;
  const frameColor = colore ?? C_CORNICE;

  const handleClick = (e: any) => { if (isDoorClosed) { e.stopPropagation(); onRemove(); } };
  const handleOver  = (e: any) => { if (isDoorClosed) { e.stopPropagation(); document.body.style.cursor = "pointer"; } };
  const handleOut   = () => { document.body.style.cursor = "default"; };

  const numLamelle      = 7;
  const lamellaThickness = MM(7);
  const lamellaLength   = innerW * 0.97;
  const spacing         = (innerH - lamellaThickness * numLamelle) / (numLamelle - 1);
  const lamellaAngle    = -32 * (Math.PI / 180);

  return (
    <group position={position}>
      {/* Cornice esterna */}
      <mesh position={[0, 0, 0]} onClick={handleClick} onPointerOver={handleOver} onPointerOut={handleOut}>
        <boxGeometry args={[gW, gH, spessore]} />
        <meshStandardMaterial color={frameColor} metalness={0.25} roughness={0.75} />
        <Edges color={C_BORDO_SCURO} />
      </mesh>

      {/* Cornice interna rientrata */}
      <mesh position={[0, 0, spessore * 0.12]} onClick={handleClick} onPointerOver={handleOver} onPointerOut={handleOut}>
        <boxGeometry args={[gW - MM(10), gH - MM(10), spessore * 0.65]} />
        <meshStandardMaterial color={frameColor} metalness={0.35} roughness={0.65} />
      </mesh>

      {/* Lamelle orizzontali inclinate */}
      {Array.from({ length: numLamelle }).map((_, i) => {
        const yPos = -innerH / 2 + lamellaThickness / 2 + i * (lamellaThickness + spacing);
        return (
          <mesh
            key={`lamella-${i}`}
            position={[0, yPos, spessore * 0.4]}
            rotation={[lamellaAngle, 0, 0]}
            onClick={handleClick}
            onPointerOver={handleOver}
            onPointerOut={handleOut}
          >
            <boxGeometry args={[lamellaLength, lamellaThickness, spessore * 0.9]} />
            <meshStandardMaterial color="#6f6658" metalness={0.55} roughness={0.45} />
          </mesh>
        );
      })}

      {/* Rinforzi verticali */}
      {Array.from({ length: 5 }).map((_, i) => {
        const x = -innerW / 2 + (innerW / 4) * i;
        return (
          <mesh
            key={`rinforzo-${i}`}
            position={[x, 0, spessore * 0.55]}
            onClick={handleClick}
            onPointerOver={handleOver}
            onPointerOut={handleOut}
          >
            <boxGeometry args={[MM(3.5), innerH * 0.88, spessore * 0.4]} />
            <meshStandardMaterial color={C_BORDO_CHIARO} metalness={0.6} roughness={0.5} />
          </mesh>
        );
      })}

      {/* Ombra interna */}
      <mesh position={[0, 0, spessore * 0.65]}>
        <boxGeometry args={[innerW - MM(12), innerH - MM(12), MM(1.5)]} />
        <meshStandardMaterial color="#444444" transparent opacity={0.6} />
      </mesh>
    </group>
  );
};
